import ReactGA from 'react-ga4';
import { IStartConversationPayloadType } from 'src/types/serviceResponseTypes';
import { GOOGLE_ANALYTICS } from 'src/app/constants';
import { ConversationAtomType } from 'src/types/generic';
import { isTelemetryEnabled } from '../utils/getTelemetrySwitch';
import { getRuntimeEnvironment } from '../utils/getRuntimeEnvironment';

export function sendLaunchEvent(frameParams: IStartConversationPayloadType) {
  if (isTelemetryEnabled() || getRuntimeEnvironment() !== 'prod') return;

  ReactGA.initialize(GOOGLE_ANALYTICS.KEY, {
    gaOptions: {
      cookieFlags: 'max-age=7200;secure;samesite=none',
    },
  });

  const data = {
    exercise_id: frameParams.exercise_id,
    book_code: frameParams.book_code,
  };

  const event_params = {
    category: GOOGLE_ANALYTICS.CATEGORY,
    action: GOOGLE_ANALYTICS.EVENTS.LAUNCH_EVENT,
    ...data,
  };

  // Send GA4 Event
  ReactGA.event(GOOGLE_ANALYTICS.EVENTS.LAUNCH_EVENT, event_params);
}

export function sendConversationStartedEvent(
  conversation: ConversationAtomType,
  frameParams: IStartConversationPayloadType
) {
  if (isTelemetryEnabled() || getRuntimeEnvironment() !== 'prod') return;

  const data = {
    exercise_id: frameParams.exercise_id,
    book_code: frameParams.book_code,
    conversation_id: conversation.data.id,
  };

  const event_params = {
    category: GOOGLE_ANALYTICS.CATEGORY,
    action: GOOGLE_ANALYTICS.EVENTS.CONVERSATION_STARTED_EVENT,
    ...data,
  };

  // Send GA4 Event
  ReactGA.event(GOOGLE_ANALYTICS.EVENTS.CONVERSATION_STARTED_EVENT, event_params);
}

export function sendInputEvent(
  action: string,
  conversations: ConversationAtomType,
  frameParams: IStartConversationPayloadType
) {
  if (isTelemetryEnabled() || getRuntimeEnvironment() !== 'prod') return;

  const message_id = conversations.data.messages[conversations.data.messages.length - 1].id;

  const data = {
    exercise_id: frameParams.exercise_id,
    book_code: frameParams.book_code,
    conversation_id: conversations.data.id,
    message_id: message_id,
  };

  const event_params = {
    category: GOOGLE_ANALYTICS.CATEGORY,
    action,
    ...data,
  };

  // Send GA4 Event
  ReactGA.event(action, event_params);
}

export function sendConversationClosedEvent(
  conversation: ConversationAtomType,
  frameParams: IStartConversationPayloadType
) {
  if (isTelemetryEnabled() || getRuntimeEnvironment() !== 'prod') return;

  if (!conversation?.data?.messages) {
    return;
  }

  //Total number of MC questions displayed
  const mcQuestions = conversation.data.messages.filter(
    (message) => message.content_type === 'multiple_choice'
  );
  const mcQuestionsDisplayed = mcQuestions.length;

  //Total number of MC questions answered correctly
  const mcQuestionsAnsweredCorrectly = mcQuestions.filter(
    (message) => message.is_correct === true
  ).length;

  //Total number of MC questions answered incorrectly
  const mcQuestionsAnsweredIncorrectly = mcQuestionsDisplayed - mcQuestionsAnsweredCorrectly;

  //Total number of MC questions with nudges
  const mcQuestionsWithNudges = mcQuestions.filter((message) => message.nudge !== null).length;

  //Total number of incorrect attempts
  const incorrectAttempts = mcQuestions.filter(
    (message) => (message.selected_choices?.length ?? 0) > 1
  ).length;

  //End of conversation reached
  const endOfConversationReached =
    mcQuestions.filter((message) => message.locked === true).length === mcQuestionsDisplayed;

  const data = {
    exercise_id: frameParams.exercise_id,
    book_code: frameParams.book_code,
    conversation_id: conversation.data.id,
    total_questions: mcQuestionsDisplayed,
    total_correct: mcQuestionsAnsweredCorrectly,
    total_incorrect: mcQuestionsAnsweredIncorrectly,
    total_nudges: mcQuestionsWithNudges,
    total_incorrect_attempts: incorrectAttempts,
    end_of_conversation_reached: endOfConversationReached,
  };

  const event_params = {
    category: GOOGLE_ANALYTICS.CATEGORY,
    action: GOOGLE_ANALYTICS.EVENTS.CONVERSATION_CLOSED_EVENT,
    ...data,
  };

  // Send GA4 Event
  ReactGA.event(GOOGLE_ANALYTICS.EVENTS.CONVERSATION_CLOSED_EVENT, event_params);
}
